import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import '../../assets/styles/account/account.scss';
import usePageMeta from '../../services/page-meta';
import {buildFormBody, parseJsonResponse, validatePassword} from './helpers';

export default function ChangePasswordByHashPage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.changePasswordHash.title', t('restorePassword.newPasswordHeader')),
        description: t('meta.changePasswordHash.description', t('app.description')),
        keywords: t('meta.changePasswordHash.keywords', t('app.keywords')),
    });

    const location = useLocation();
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordRepeat, setNewPasswordRepeat] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [newPasswordRepeatError, setNewPasswordRepeatError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const hash = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('hash') || '';
    }, [location.search]);

    useEffect(() => {
        let isCancelled = false;

        fetch(`/api/v1/account/change-password${location.search || ''}`, {credentials: 'same-origin'})
            .then(parseJsonResponse)
            .then(({response, data}) => {
                if (isCancelled) {
                    return;
                }

                if (!response.ok) {
                    throw data;
                }

                if (data?.state === 'redirect' && data?.redirectUrl) {
                    window.location.href = data.redirectUrl;
                    return;
                }

                if (data?.state !== 'active' || !data?.hash) {
                    window.location.href = '/';
                    return;
                }

                setPayload(data);
            })
            .catch((errorPayload) => {
                if (isCancelled) {
                    return;
                }

                const redirectUrl = errorPayload?.redirectUrl || '/';
                window.location.href = redirectUrl;
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [location.search]);

    const resolvePasswordErrorText = (code) => {
        if (code === 'required') {
            return t('restorePassword.errors.requiredPassword');
        }
        if (code === 'min') {
            return t('restorePassword.errors.passwordMinLength');
        }
        if (code === 'max') {
            return t('restorePassword.errors.passwordMaxLength');
        }
        if (code === 'format') {
            return t('restorePassword.errors.passwordWrongFormat');
        }

        return '';
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (isSubmitting || !payload) {
            return;
        }

        setSubmitError('');

        const passwordCode = validatePassword(newPassword);
        const repeatCode = validatePassword(newPasswordRepeat);
        const passwordError = resolvePasswordErrorText(passwordCode);
        let repeatError = resolvePasswordErrorText(repeatCode);

        if (!repeatError && newPassword !== newPasswordRepeat) {
            repeatError = t('restorePassword.errors.passwordMismatch');
        }

        setNewPasswordError(passwordError);
        setNewPasswordRepeatError(repeatError);

        if (passwordError || repeatError) {
            return;
        }

        setIsSubmitting(true);

        fetch(payload?.actions?.changePasswordUrl || '/api/v1/account/change-password', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            body: buildFormBody({
                'new-password': newPassword,
                'new-password-repeat': newPasswordRepeat,
                hash: payload?.hash || hash,
            }),
        })
            .then(parseJsonResponse)
            .then(({response, data}) => {
                if (!response.ok) {
                    throw data;
                }

                window.location.href = data?.data?.url || '/';
            })
            .catch((errorPayload) => {
                setSubmitError(errorPayload?.error?.message || t('restorePassword.errors.changeFailed'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (isLoading) {
        return <section className="section flex-center" />;
    }

    return (
        <section className="section flex-center">
            <div className="authentication-form">
                <h1>{t('restorePassword.newPasswordHeader')}</h1>
                <div className="set-new-password">
                    <form className="form" action="#" method="post" id="change-password" onSubmit={handleSubmit}>
                        <div className="form-group block-static-height field_block">
                            <label htmlFor="new-password" className={`form-label${newPassword ? ' active' : ''}`}>
                                {t('restorePassword.newPasswordLabel')}
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="new-password"
                                id="new-password"
                                className={`form${newPasswordError ? ' error-field' : ''}`}
                                value={newPassword}
                                onChange={(event) => {
                                    setNewPassword(event.target.value);
                                    setNewPasswordError('');
                                    setSubmitError('');
                                }}
                                required
                            />
                            <div className="hide-show-link js-show-password-restore">
                                <span onClick={() => setShowPassword((current) => !current)}>
                                    {showPassword ? t('restorePassword.hide') : t('restorePassword.show')}
                                </span>
                            </div>
                            <span className={`error_text${newPasswordError ? '' : ' hidden'}`}>{newPasswordError}</span>
                        </div>
                        <div className="form-group block-static-height field_block">
                            <label htmlFor="new-password-repeat" className={`form-label${newPasswordRepeat ? ' active' : ''}`}>
                                {t('restorePassword.newPasswordRepeatLabel')}
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="new-password-repeat"
                                id="new-password-repeat"
                                className={`form${newPasswordRepeatError ? ' error-field' : ''}`}
                                value={newPasswordRepeat}
                                onChange={(event) => {
                                    setNewPasswordRepeat(event.target.value);
                                    setNewPasswordRepeatError('');
                                    setSubmitError('');
                                }}
                                required
                            />
                            <div className="hide-show-link js-show-password-restore">
                                <span onClick={() => setShowPassword((current) => !current)}>
                                    {showPassword ? t('restorePassword.hide') : t('restorePassword.show')}
                                </span>
                            </div>
                            <span className={`error_text${newPasswordRepeatError ? '' : ' hidden'}`}>{newPasswordRepeatError}</span>
                        </div>
                        <div className={`error-block${submitError ? '' : ' hidden'}`}>
                            <div className="error_text">{submitError}</div>
                        </div>
                        <div className="submit-block">
                            <input type="submit" value={t('restorePassword.save')} className="btn" disabled={isSubmitting} />
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
